import {
  BaseQueryApi,
  BaseQueryFn,
  DefinitionType,
  FetchArgs,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import { RootState } from "../store";
import { logOut, setUser } from "../feature/auth/authSlice";
const baseUrl =
  import.meta.env.VITE_NODE_ENV === "development"
    ? import.meta.env.VITE_API_BASE_URL_LOCAL
    : import.meta.env.VITE_NODE_ENV === "stage"
      ? import.meta.env.VITE_API_BASE_URL_STAGE
      : import.meta.env.VITE_NODE_ENV === "dev"
        ? import.meta.env.VITE_API_BASE_URL_DEV
        : import.meta.env.VITE_API_BASE_URL_PROD;
const baseQuery = fetchBaseQuery({
  baseUrl: baseUrl,
  credentials: "include",

  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) {
      headers.set("authorization", token);
    }
    return headers;
  },
});

/** Single in-flight refresh so parallel 401s do not stampede the server. */
let refreshAccessTokenPromise: Promise<string | null> | null = null;

async function fetchNewAccessTokenFromCookie(): Promise<string | null> {
  try {
    const res = await fetch(`${baseUrl}/auth/refresh-token`, {
      method: "GET",
      credentials: "include",
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.data?.accessToken ?? null;
  } catch {
    return null;
  }
}

const baseQueryWithRefreshToken: BaseQueryFn<
  FetchArgs,
  BaseQueryApi,
  DefinitionType
> = async (args, api, extraOptions): Promise<any> => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error?.status !== 401) {
    return result;
  }

  if (!refreshAccessTokenPromise) {
    refreshAccessTokenPromise = fetchNewAccessTokenFromCookie().finally(() => {
      refreshAccessTokenPromise = null;
    });
  }

  const accessToken = await refreshAccessTokenPromise;

  if (!accessToken) {
    api.dispatch(logOut());
    return result;
  }

  const user = (api.getState() as RootState).auth.user;
  api.dispatch(setUser({ user, token: accessToken }));
  result = await baseQuery(args, api, extraOptions);
  return result;
};

export const baseApi = createApi({
  reducerPath: "baseApi",
  baseQuery: baseQueryWithRefreshToken,
  tagTypes: [
    "videoScript",
    "videoSetup",
    "actorSelection",
    "User",
    "requestedActor",
    "video-editing",
    "Order",
    "OrderDetails",
    "rawFootage",
    "Notifications",
    "Template",
    "CreatorJobs",
    "CreatorProjects",
    "ProductShipping",
    "CreatorBalance",
    "CreatorTransactions",
    "WithdrawnRequest",
    "PaymentMethods",
    "VideoChange",
    "shippingAddress",
    "creatorStatus",
    "ProjectRequest",
    "sameDayDelivery",
    "CreatorProjectDetails",
    "OrderVideoStage",
  ],
  endpoints: () => ({}),
});
